import { useEffect, useMemo, useRef } from 'react';
import { Chart } from 'react-chartjs-2';
import { AutoSizer, Size } from 'react-virtualized';
import { Chart as ChartJS, registerables } from 'chart.js';
import { CandlestickController, CandlestickElement, OhlcElement } from 'chartjs-chart-financial';
import zoomPlugin from 'chartjs-plugin-zoom';
import { candle_default } from 'client/voladeum/candles/defaults';
import { updateSettings } from 'ducks/settings/slices';
import { useAppDispatch } from 'store/hooks';
import {
  getCandlesData,
  getPriceAxis,
  getTimeseriesAxis,
  getVolumeAxis,
  getVolumeData,
} from 'utils/charts';
import { populateCandle, retrieveCandle } from 'utils/objects';

import { Card } from 'components/cards/Card';
import { crosshairPlugin } from 'components/plugins/Crosshairs';
import { pricePlugin } from 'components/plugins/Price';

import 'chartjs-adapter-moment';

import { CANDLE_WINDOW, EMPTY_ARRAY } from '../../../../constants';

import { CandleGraphProps } from './types';

ChartJS.register(
  ...registerables,
  CandlestickController,
  CandlestickElement,
  OhlcElement,
  pricePlugin,
  crosshairPlugin,
  zoomPlugin
);

export const CandleOHLCV = ({ visible, candles = EMPTY_ARRAY }: CandleGraphProps) => {
  const dispatch = useAppDispatch();
  const candlesRef = useRef<ChartJS>(null);

  useEffect(() => {
    if (candles.length > 0) {
      const { current, previous } = retrieveCandle(candles, candles.length - 1);
      dispatch(updateSettings({ current, previous }));
    } else {
      dispatch(
        updateSettings({
          current: populateCandle(candle_default),
          previous: populateCandle(candle_default),
        })
      );
    }
  }, [candles, candles.length, dispatch]);

  const timeseries = useMemo(() => {
    if (candles.length === 0) {
      return { min: 0, max: CANDLE_WINDOW };
    }
    return { min: candles[0].x, max: candles[candles.length - 1].x };
  }, [candles]);

  const scale = useMemo(() => {
    const min = Math.min(...candles.map((candle) => candle.c));
    const max = Math.max(...candles.map((candle) => candle.c));
    return max - min;
  }, [candles]);

  const price_candle = getCandlesData({ data: candles });
  const volume = getVolumeData({ data: candles });
  const candlesticks = { datasets: [price_candle, volume] };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: getTimeseriesAxis({ min: visible.start, max: visible.end, display: false }),
      line: getPriceAxis({ scale, buffer: 0.2 }),
      volume: getVolumeAxis({ multiplier: 3 }),
    },
    layout: { padding: { right: 0 } },
    plugins: {
      legend: { display: false },
      crosshairs: { enabled: true },
      price: { enabled: true },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          const tooltip = context.tooltip;

          if (!tooltip.dataPoints?.length) return;

          if (tooltip.opacity === 0) {
            const { current, previous } = retrieveCandle(candles, candles.length - 1);
            dispatch(updateSettings({ current, previous }));
            return;
          }

          const data = tooltip.dataPoints[0];
          const index = data.dataIndex;
          const { current, previous } = retrieveCandle(candles, index);
          dispatch(updateSettings({ current, previous }));
        },
      },
      zoom: { limits: { x: { min: timeseries.min, max: timeseries.max } } },
    },
  };

  return (
    <Card minHeight="350px">
      <AutoSizer>
        {({ height, width }: Size) => (
          <div style={{ position: 'relative', height, width }}>
            <Chart
              ref={candlesRef}
              key={`${height}${width}`}
              type="candlestick"
              data={candlesticks}
              options={options}
              width={width}
              height={height}
            />
          </div>
        )}
      </AutoSizer>
    </Card>
  );
};
